/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import compact from "lodash/compact";
import filter from "lodash/filter";
import map from "lodash/map";
import some from "lodash/some";

import { FilterValueFieldNames } from "Constants/FilterFields";
import { OperationStatus } from "Constants/OperationStatus";
import { AppState } from "Dataflows/Reducers";
import { IApplicationFlagResponse } from "Interfaces/Application/IApplicationFlagResponse";
import { IApplicationFormActivityEvent } from "Interfaces/Application/IApplicationFormActivityEvent";
import { IApplicationQuestionResponse } from "Interfaces/Application/IApplicationQuestionResponse";
import { IApplicationUserProfileInformation } from "Interfaces/Application/IApplicationUserProfileInformation";
import { IFilterValue } from "Interfaces/IFilterValue";
import store from "Store";

export const getSelectedHighlightedActivitiesEventsFilterValues = (
  state: AppState
): IFilterValue[] =>
  state.applicationDetailsState.highlightedActivitiesEventsState.activitiesEventsFilterValues;

export const getSelectedAdditionalActivitiesEventsFilterValues = (
  state: AppState
): IFilterValue[] =>
  state.applicationDetailsState.additionalActivitiesEventsState.activitiesEventsFilterValues;

export const selectTechnologyAreaFilterValuesByAreaGroups = (
  areaGroups: string[]
): IFilterValue[] => {
  const rootState = store.getState();
  const techAreas = filter(rootState.technologyAreaDataState.technologyAreaData, (y) =>
    some(areaGroups, (x) => x === y.technologyFocusAreaGroupName)
  );
  return compact(
    map(techAreas, (item) => {
      return { fieldName: FilterValueFieldNames.TECHNOLOGY_AREA, value: item.technologyName };
    })
  );
};

export const getHighlightedActivitiesEventsCurrentPage = (state: AppState): number =>
  state.applicationDetailsState.highlightedActivitiesEventsState.currentPage;

export const getHighlightedActivitiesEventsFilteredCount = (state: AppState): number =>
  state.applicationDetailsState.highlightedActivitiesEventsState.filteredCount;

export const getHighlightedActivitiesEventsPageSize = (state: AppState): number =>
  state.applicationDetailsState.highlightedActivitiesEventsState.pageSize;

export const getAdditionalActivitiesEventsCurrentPage = (state: AppState): number =>
  state.applicationDetailsState.additionalActivitiesEventsState.currentPage;

export const getAdditionalActivitiesEventsFilteredCount = (state: AppState): number =>
  state.applicationDetailsState.additionalActivitiesEventsState.filteredCount;

export const getAdditionalActivitiesEventsPageSize = (state: AppState): number =>
  state.applicationDetailsState.additionalActivitiesEventsState.pageSize;

/**
 * Returns the boolean value for whether or not the user profile information is still being loaded.
 * @param {AppState} state The application state.
 * @returns {boolean} True if the user profile information is still being loaded.
 */
export const isLoadingUserProfileInfoBySelectedApplicationId = (state: AppState): boolean =>
  state.applicationDetailsState.selectedUserProfileInformationOpStatus?.read ===
  OperationStatus.Pending;

/**
 * Returns the error message for the user profile information fetch by application id.
 * @param {AppState} state The application state.
 * @returns {string | undefined} The error message for the user profile information fetch by application id.
 */
export const getUserProfileInfoBySelectedApplicationIdErrorMessage = (
  state: AppState
): string | undefined => state.applicationDetailsState.selectedUserProfileInformationErrorMessage;

/**
 * Returns the user profile information for the selected application id.
 * @param {AppState} state The application state.
 * @returns {IApplicationUserProfileInformation | undefined} The user profile information for the selected application id.
 */
export const getUserProfileInfoBySelectedApplicationId = (
  state: AppState
): IApplicationUserProfileInformation | undefined =>
  state.applicationDetailsState.selectedUserProfileInformation;

/**
 * Returns the application questions for the selected application id.
 * @param {AppState} state The application state.
 * @returns {IApplicationQuestionResponse[] | undefined} The application questions for the selected application id.
 */
export const getApplicationQuestionsBySelectedApplicationId = (
  state: AppState
): IApplicationQuestionResponse[] | undefined =>
  state.applicationDetailsState.selectedApplicationQuestions;

/**
 * Returns the error message for the application questions fetch by application id.
 * @param {AppState} state The application state.
 * @returns {string | undefined} The error message for the application questions fetch by application id.
 */
export const getApplicationQuestionsBySelectedApplicationIdErrorMessage = (
  state: AppState
): string | undefined => state.applicationDetailsState.selectedApplicationQuestionsErrorMessage;

/**
 * Returns the boolean value for whether or not the application questions are still being loaded.
 * @param {AppState} state The application state.
 * @returns {boolean} True if the application questions are still being loaded.
 */
export const getIsLoadingApplicationQuestionsBySelectedApplicationId = (state: AppState): boolean =>
  state.applicationDetailsState.selectedApplicationQuestionsOpStatus?.read ===
  OperationStatus.Pending;

/**
 * Returns the highlighted activities or events for the selected application id.
 * @param {AppState} state The application state.
 * @returns {IApplicationFormActivityEvent[] | undefined} The highlighted activities or events for the selected application id.
 */
export const getHighlightedActivitiesEventsBySelectedApplicationId = (
  state: AppState
): IApplicationFormActivityEvent[] | undefined =>
  state.applicationDetailsState.highlightedActivitiesEventsState.activitiesEvents;

/**
 * Returns the error message for the highlighted activities or events fetch by application id.
 * @param {AppState} state The application state.
 * @returns {string | undefined} The error message for the highlighted activities or events fetch by application id.
 */
export const getHighlightedActivitiesEventsBySelectedApplicationIdErrorMessage = (
  state: AppState
): string | undefined =>
  state.applicationDetailsState.highlightedActivitiesEventsState.activitiesEventsErrorMessage;

/**
 * Returns the boolean value for whether or not the highlighted activities or events are still being loaded.
 * @param {AppState} state The application state.
 * @returns {boolean} True if the highlighted activities or events are still being loaded.
 */
export const getIsLoadingHighlightedActivitiesEventsBySelectedApplicationId = (
  state: AppState
): boolean =>
  state.applicationDetailsState.highlightedActivitiesEventsState.activitiesEventsOpStatus?.read ===
  OperationStatus.Pending;

/**
 * Returns the highlighted activities or events for the selected application id.
 * @param {AppState} state The application state.
 * @returns {IApplicationFormActivityEvent[] | undefined} The highlighted activities or events for the selected application id.
 */
export const getAdditionalActivitiesEventsBySelectedApplicationId = (
  state: AppState
): IApplicationFormActivityEvent[] | undefined =>
  state.applicationDetailsState.additionalActivitiesEventsState.activitiesEvents;

/**
 * Returns the error message for the highlighted activities or events fetch by application id.
 * @param {AppState} state The application state.
 * @returns {string | undefined} The error message for the highlighted activities or events fetch by application id.
 */
export const getAdditionalActivitiesEventsBySelectedApplicationIdErrorMessage = (
  state: AppState
): string | undefined =>
  state.applicationDetailsState.additionalActivitiesEventsState.activitiesEventsErrorMessage;

/**
 * Returns the boolean value for whether or not the highlighted activities or events are still being loaded.
 * @param {AppState} state The application state.
 * @returns {boolean} True if the highlighted activities or events are still being loaded.
 */
export const getIsLoadingAdditionalActivitiesEventsBySelectedApplicationId = (
  state: AppState
): boolean =>
  state.applicationDetailsState.additionalActivitiesEventsState.activitiesEventsOpStatus?.read ===
  OperationStatus.Pending;

export const getIsLoadingActivityEventDetails = (state: AppState): boolean =>
  state.applicationDetailsState.selectedActivityEventOpStatus?.read === OperationStatus.Pending;

export const getSelectedActivityEvent = (
  state: AppState
): IApplicationFormActivityEvent | undefined => state.applicationDetailsState.selectedActivityEvent;

export const getSelectedActivityEventErrorMessage = (state: AppState): string | undefined =>
  state.applicationDetailsState.selectedActivityEventErrorMessage;

export const getApplicationFlagsBySelectedApplicationId = (
  state: AppState
): IApplicationFlagResponse[] | undefined => state.applicationDetailsState.selectedApplicationFlags;

export const getIsLoadingApplicationFlagsBySelectedApplicationId = (state: AppState): boolean =>
  state.applicationDetailsState.selectedApplicationFlagsOpStatus?.read === OperationStatus.Pending;

export const getApplicationFlagsBySelectedApplicationIdErrorMessage = (
  state: AppState
): string | undefined => state.applicationDetailsState.selectedApplicationFlagsErrorMessage;

export const getSelectedUserProfileApplicationId = (state: AppState): number | undefined =>
  state.applicationDetailsState.selectedUserProfileInformation?.applicationFormId;

export const getManageApplicationOperationStatus = (state: AppState): OperationStatus | undefined =>
  state.applicationDetailsState.manageApplicationOpStatus?.update;

export const manageApplicationErrorMessage = (state: AppState): string | undefined =>
  state.applicationDetailsState.manageApplicationErrorMessage;

export const getUpdateTechnologyAreaOperationStatus = (
  state: AppState
): OperationStatus | undefined =>
  state.applicationDetailsState.updateTechnologyAreaOpStatus?.update;

export const updateTechnologyAreaErrorMessage = (state: AppState): string | undefined =>
  state.applicationDetailsState.updateTechnologyAreaErrorMessage;
